import { canHost } from 'peer-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePeerService } from '../hooks/usePeerService.js'
import { BotConfigModal } from './BotConfigModal'
import { ChatInterface } from './ChatInterface'
import { ConfirmDialog } from './ConfirmDialog'
import { ContactDetail } from './ContactDetail'
import { ContactList } from './ContactList'
import { ConversationSidebar } from './ConversationSidebar'
import { LlmBotEditor } from './LlmBotEditor'
import { ProfilePanel } from './ProfilePanel'
import './AppLayout.css'

const ChatsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
)

const ContactsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
)

const NAV_ITEMS = [
  { id: 'chats', Icon: ChatsIcon, title: 'Chats' },
  { id: 'contacts', Icon: ContactsIcon, title: 'Contacts' },
]

const PANEL_TITLES: Record<string, string> = {
  chats: 'Chats',
  contacts: 'Contacts',
  profile: 'Profile',
}

export const AppLayout = ({
  api,
  onAuthError,
  onLogout,
  userInfo,
  peerService,
  roleCall: _roleCall,
  emptyMessage = 'How can I help you today?',
  placeholder = 'Message MyAgent...',
}: any) => {
  // Navigation
  const [activeTab, setActiveTab] = useState(() => {
    const h = location.hash.slice(1)
    if (h.startsWith('conv/')) return 'chats'
    return ['contacts', 'profile'].includes(h) ? h : 'chats'
  })
  const [showSidebar, setShowSidebar] = useState(
    () => !(location.hash.startsWith('#conv/') && window.innerWidth <= 768),
  )

  // Conversation state
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() =>
    location.hash.startsWith('#conv/') ? location.hash.slice(6) : null,
  )
  const [conversationIndex, setConversationIndex] = useState<any[]>([])

  // Contact detail
  const [selectedContact, setSelectedContact] = useState<any>(null)

  // Modals
  const [configBot, setConfigBot] = useState<any>(null)
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Peers (unified contacts: LLM bots + worker roles)
  const { peers, bots: _llmBots, refresh: refreshPeers } = usePeerService(peerService)

  // Show only peers capable of hosting a chat (LLM bots + chat-type workers)
  const visiblePeers = useMemo(() => peers.filter((p: any) => canHost(p)), [peers])

  const [editingBot, setEditingBot] = useState<any>(undefined) // undefined=hidden, null=new, object=editing

  // LLM bot CRUD handlers
  const handleSaveBot = useCallback(
    async (config: any) => {
      if (!peerService) return
      if (config.botId) {
        await peerService.updateBot(config.botId, config)
      } else {
        await peerService.addBot(config)
      }
      await refreshPeers()
      setEditingBot(undefined)
    },
    [peerService, refreshPeers],
  )

  const handleDeleteBot = useCallback(
    async (botId: string) => {
      if (!peerService) return
      await peerService.removePeer(botId)
      await refreshPeers()
      setEditingBot(undefined)
      if (selectedContact?.roleId === botId) setSelectedContact(null)
    },
    [peerService, refreshPeers, selectedContact],
  )

  // URL hash sync
  useEffect(() => {
    let newHash = ''
    if (activeTab === 'chats' && activeConversationId) newHash = `#conv/${activeConversationId}`
    else if (activeTab !== 'chats') newHash = `#${activeTab}`
    if (location.hash !== newHash) history.replaceState(null, '', newHash || location.pathname)
  }, [activeTab, activeConversationId])

  // Resolve active conversation entry
  const activeConv = activeConversationId
    ? conversationIndex.find((c: any) => c.id === activeConversationId)
    : null

  // Resolve host peer from conv_index hostRoleId
  const activeHost = useMemo(() => {
    if (!activeConv) return null
    const peer = peers.find((p: any) => p.roleId === activeConv.hostRoleId)
    if (peer) return peer
    // Fallback: unknown host
    return {
      roleId: activeConv.hostRoleId,
      type: 'worker',
      displayName: activeConv.title || 'Bot',
      online: false,
    }
  }, [activeConv, peers])

  // Resolve participant peers from conv_index participantRoleIds
  const activeParticipants = useMemo(() => {
    if (!activeConv?.participantRoleIds?.length) return []
    return activeConv.participantRoleIds
      .map((id: string) => peers.find((p: any) => p.roleId === id))
      .filter(Boolean)
  }, [activeConv, peers])

  // Load conversation index
  useEffect(() => {
    if (!api) return
    api
      .getConversationIndex()
      .then((index: any[]) => {
        setConversationIndex(index)
        const hashId = location.hash.startsWith('#conv/') ? location.hash.slice(6) : null
        if (hashId && !index.find((c: any) => c.id === hashId)) setActiveConversationId(null)
      })
      .catch((err: any) => console.warn('Failed to load conversation index:', err.message))
  }, [api])

  // --- Participant management (per-conversation) ---

  const addParticipant = useCallback(
    async (role: any) => {
      if (!activeConversationId || !activeConv) return
      if (activeConv.participantRoleIds?.includes(role.roleId)) return
      const updated = [...(activeConv.participantRoleIds || []), role.roleId]
      setConversationIndex(prev =>
        prev.map((c: any) =>
          c.id === activeConversationId ? { ...c, participantRoleIds: updated } : c,
        ),
      )
      await api.setParticipants(activeConversationId, updated)
    },
    [api, activeConversationId, activeConv],
  )

  const removeParticipant = useCallback(
    async (roleId: string) => {
      if (!activeConversationId || !activeConv) return
      const updated = (activeConv.participantRoleIds || []).filter((id: string) => id !== roleId)
      setConversationIndex(prev =>
        prev.map((c: any) =>
          c.id === activeConversationId ? { ...c, participantRoleIds: updated } : c,
        ),
      )
      await api.setParticipants(activeConversationId, updated)
    },
    [api, activeConversationId, activeConv],
  )

  const handleDeleteRole = useCallback(
    async (roleId: string) => {
      if (!peerService) return
      await peerService.removePeer(roleId)
      await refreshPeers()
      setSelectedContact((prev: any) => (prev?.roleId === roleId ? null : prev))
      // Remove from all conversations
      for (const conv of conversationIndex) {
        if (conv.participantRoleIds?.includes(roleId)) {
          const updated = conv.participantRoleIds.filter((id: string) => id !== roleId)
          await api.setParticipants(conv.id, updated)
        }
      }
      setConversationIndex(prev =>
        prev.map((c: any) => ({
          ...c,
          participantRoleIds: (c.participantRoleIds || []).filter((id: string) => id !== roleId),
        })),
      )
    },
    [peerService, refreshPeers, conversationIndex, api],
  )

  // --- Conversation handlers ---
  const openConversation = useCallback((conv: any) => {
    setConversationIndex(prev => [...prev, conv])
    setActiveConversationId(conv.id)
    setActiveTab('chats')
    setSelectedContact(null)
    setShowSidebar(false)
  }, [])

  const createConv = useCallback(
    async (hostRoleId: string, title: string) => {
      return await api.createConversation({ hostRoleId, title })
    },
    [api],
  )

  const handleNewConversation = useCallback(
    async (peer: any) => {
      if (!canHost(peer)) return
      const existing = conversationIndex.filter((c: any) => c.hostRoleId === peer.roleId).length
      const title = existing > 0 ? `${peer.displayName} (${existing + 1})` : peer.displayName
      openConversation(await createConv(peer.roleId, title))
    },
    [conversationIndex, createConv, openConversation],
  )

  const handleRenameConversation = useCallback(
    async (convId: string, title: string) => {
      await api.renameConversation(convId, title)
      setConversationIndex(prev => prev.map((c: any) => (c.id === convId ? { ...c, title } : c)))
    },
    [api],
  )

  const handleSwitchConversation = useCallback((convId: string) => {
    setActiveConversationId(convId)
    setShowSidebar(false)
  }, [])

  const handleDeleteConversation = useCallback(
    async (convId: string) => {
      await api.deleteConversation(convId)
      setConversationIndex(prev => prev.filter((c: any) => c.id !== convId))
      if (activeConversationId === convId) setActiveConversationId(null)
    },
    [api, activeConversationId],
  )

  const confirmClearChat = async () => {
    try {
      await api.deleteConversation(activeConversationId)
      setConversationIndex(prev => prev.filter((c: any) => c.id !== activeConversationId))
      setActiveConversationId(null)
    } catch (error: any) {
      setErrorMessage(`Failed to delete conversation: ${error.message}`)
    }
    setShowClearDialog(false)
  }

  const handleBack = useCallback(() => {
    setShowSidebar(true)
  }, [])

  const handleStartChat = useCallback(
    async (contact: any) => {
      // Non-hostable peers → open conversation with default host
      const hostId = canHost(contact)
        ? contact.roleId
        : peers.find((p: any) => p.type === 'llm-bot' && p.roleType === 'host')?.roleId
      if (!hostId) return
      const existing = conversationIndex.find((c: any) => c.hostRoleId === hostId)
      if (existing) {
        setActiveConversationId(existing.id)
        setActiveTab('chats')
        setSelectedContact(null)
        setShowSidebar(false)
      } else {
        openConversation(await createConv(hostId, contact.displayName))
      }
    },
    [conversationIndex, peers, createConv, openConversation],
  )

  const chatTitle = activeConv ? activeConv.title || 'New conversation' : ''
  const onRenameChat = useCallback(
    (title: string) => handleRenameConversation(activeConversationId!, title),
    [handleRenameConversation, activeConversationId],
  )
  const inputPlaceholder = activeHost ? `Message ${activeHost.displayName}...` : placeholder

  // User avatar for nav rail
  const avatarInitial = (() => {
    const name = userInfo?.name || userInfo?.email?.split('@')[0] || ''
    return name.charAt(0).toUpperCase() || '?'
  })()

  return (
    <div className="app-layout">
      <BotConfigModal
        isOpen={!!configBot}
        onClose={() => setConfigBot(null)}
        roleId={configBot?.roleId}
        name={configBot?.displayName}
        peerService={peerService}
      />

      <ConfirmDialog
        isOpen={showClearDialog}
        onClose={() => setShowClearDialog(false)}
        onConfirm={confirmClearChat}
        title="Delete Conversation"
        message="Delete this conversation and all its messages?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {errorMessage && (
        <ConfirmDialog
          isOpen={!!errorMessage}
          onClose={() => setErrorMessage('')}
          onConfirm={() => setErrorMessage('')}
          title="Error"
          message={errorMessage}
          confirmText="OK"
          cancelText=""
          variant="danger"
        />
      )}

      {/* Column 1: Nav Rail */}
      <nav className="nav-rail">
        <div className="nav-rail-top">
          {NAV_ITEMS.map(({ id, Icon, title }) => (
            <button
              key={id}
              className={`nav-btn${activeTab === id ? ' nav-btn-active' : ''}`}
              onClick={() => {
                setActiveTab(id)
                if (id === 'chats' || id === 'contacts') setShowSidebar(true)
                else setShowSidebar(false)
                if (id !== 'contacts') setSelectedContact(null)
              }}
              title={title}
            >
              <Icon />
            </button>
          ))}
        </div>
        <div className="nav-rail-bottom">
          {userInfo?.avatar ? (
            <img
              src={userInfo.avatar}
              alt={userInfo.name || 'User'}
              className={`nav-avatar${activeTab === 'profile' ? ' nav-avatar-active' : ''}`}
              referrerPolicy="no-referrer"
              onClick={() => {
                setActiveTab('profile')
                setShowSidebar(false)
                setSelectedContact(null)
              }}
              onError={(e: any) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={`nav-avatar-initials${activeTab === 'profile' ? ' nav-avatar-active' : ''}`}
            style={userInfo?.avatar ? { display: 'none' } : undefined}
            onClick={() => {
              setActiveTab('profile')
              setShowSidebar(false)
              setSelectedContact(null)
            }}
          >
            {avatarInitial}
          </div>
        </div>
      </nav>

      {/* Column 2: List Panel (chats & contacts only) */}
      {(activeTab === 'chats' || activeTab === 'contacts') && (
        <div className={`list-panel${showSidebar ? '' : ' list-panel-hidden'}`}>
          <div className="list-panel-header">
            <h2 className="list-panel-title">{PANEL_TITLES[activeTab]}</h2>
          </div>
          <div className="list-panel-content">
            {activeTab === 'chats' && (
              <ConversationSidebar
                conversations={conversationIndex}
                activeConversationId={activeConversationId}
                onSelect={handleSwitchConversation}
                onDelete={handleDeleteConversation}
                onCreate={handleNewConversation}
                contacts={visiblePeers}
              />
            )}
            {activeTab === 'contacts' && (
              <>
                {peerService && (
                  <button
                    className="new-bot-btn"
                    onClick={() => {
                      setEditingBot(null)
                      setSelectedContact(null)
                    }}
                  >
                    + New LLM Bot
                  </button>
                )}
                <ContactList
                  contacts={visiblePeers}
                  onSelectContact={(c: any) => {
                    setSelectedContact(c)
                    setEditingBot(undefined)
                  }}
                  onCreateBot={() => {
                    setEditingBot(null)
                    setSelectedContact(null)
                  }}
                  selectedContactId={selectedContact?.roleId}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Column 3: Main Content */}
      <div
        className={`app-main${showSidebar && (activeTab === 'chats' || activeTab === 'contacts') ? ' app-main-hidden' : ''}`}
      >
        {activeTab === 'chats' &&
          (activeConversationId && activeHost ? (
            <ChatInterface
              api={api}
              onAuthError={onAuthError}
              peerService={peerService}
              conversationId={activeConversationId}
              host={activeHost}
              participants={activeParticipants}
              contacts={visiblePeers}
              emptyMessage={emptyMessage}
              placeholder={inputPlaceholder}
              chatTitle={chatTitle}
              onRenameChat={onRenameChat}
              onBack={handleBack}
              onAddParticipant={addParticipant}
              onRemoveParticipant={removeParticipant}
              onRefreshContacts={refreshPeers}
              onConfigureBot={setConfigBot}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-dim)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {emptyMessage || 'Select a conversation or start a new one'}
            </div>
          ))}
        {activeTab === 'contacts' && editingBot !== undefined && (
          <LlmBotEditor
            bot={editingBot}
            onSave={handleSaveBot}
            onCancel={() => setEditingBot(undefined)}
            onDelete={handleDeleteBot}
          />
        )}
        {activeTab === 'contacts' && editingBot === undefined && !selectedContact && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Select a contact to view details
          </div>
        )}
        {activeTab === 'contacts' &&
          editingBot === undefined &&
          selectedContact &&
          (selectedContact.type === 'llm-bot' ? (
            <LlmBotEditor
              bot={selectedContact.botConfig}
              onSave={handleSaveBot}
              onCancel={() => setSelectedContact(null)}
              onDelete={handleDeleteBot}
              onStartChat={(bot: any) =>
                handleStartChat({ roleId: bot.botId, displayName: bot.displayName })
              }
            />
          ) : (
            <ContactDetail
              contact={selectedContact}
              isOnline={selectedContact.online}
              peerService={peerService}
              onStartChat={handleStartChat}
              onDeleteRole={handleDeleteRole}
            />
          ))}
        {activeTab === 'profile' && <ProfilePanel userInfo={userInfo} onLogout={onLogout} />}
      </div>
    </div>
  )
}
